import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Zap, Coins, ShieldCheck, LineChart, Link2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const features = [
  {
    icon: Zap,
    title: 'Lightning Fast Transactions',
    description: 'Experience near-instant transaction finality with our optimized PoA consensus.',
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/20',
  },
  {
    icon: Coins,
    title: 'Low Gas Fees',
    description: 'Send transactions for a fraction of a cent, making DeFi accessible to all.',
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/20',
  },
  {
    icon: ShieldCheck,
    title: 'High Security',
    description: 'Battle-tested smart contracts with multiple security audits and bug bounties.',
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/20',
  },
  {
    icon: LineChart,
    title: 'Scalable Infrastructure',
    description: 'Designed to handle millions of daily transactions without compromising speed.',
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/20',
  },
  {
    icon: Link2,
    title: 'Cross-chain Compatibility',
    description: 'Seamlessly bridge assets between Polk Chain and other major networks.',
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
];

export const Features = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="features"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#050505]' : 'bg-[#F8FAFC]'}`}
      ref={ref}
      data-testid="features-section"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-blue-500/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Features
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Built for <span className="gradient-text">Performance</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            Cutting-edge technology designed to deliver the best blockchain experience.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className={`relative group p-8 rounded-3xl backdrop-blur-xl border ${feature.border} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDark ? 'bg-white/[0.03]' : 'bg-white'
              } ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
            >
              {/* Icon */}
              <div
                className={`w-16 h-16 rounded-2xl ${feature.bg} border ${feature.border} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
              >
                <feature.icon className={`w-8 h-8 ${feature.color}`} />
              </div>

              {/* Content */}
              <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {feature.title}
              </h3>
              <p className={`leading-relaxed ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
                {feature.description}
              </p>

              {/* Hover glow */}
              <div
                className={`absolute -bottom-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 ${feature.bg}`}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
